/**
 * @file config.ts
 * @description VT2 ULTRA V2 的 8 profile 配置切换、重命名（V0106 BB/BC）与恢复出厂适配。
 */

import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { ConfigFeature } from '../../../../features/config/contract'
import type { Config, DeviceDataSnapshot } from '../../../../types/config'
import type { Macro } from '../../../../types/mouseKeys'
import type { BaseDevice } from '../../../../runtime/BaseDevice'
import { formatFirmwareVersions } from '../../../../utils/firmwareVersion'
import {
  assertVisionApProfileIndex,
  decodeVisionApProfileName,
  encodeVisionApProfileName,
  getVisionApProfileNameSlotOffset,
  VISION_AP_MOUSE_FUNCTION_AREA,
  VISION_AP_MOUSE_PROFILE_NAME_AREA,
  type VisionApFunctionAreaSession,
  type VisionApKeyAreaSession,
  type VisionApProfileNameAreaSession,
} from '../../protocols/vision-ap-mouse'

const PROFILE_SNAPSHOT_SCHEMA = 'telink-3228c-profile-v1'
const PROFILE_AREA_CHUNK_LENGTHS = [24, 24, 24, 24, 24, 8] as const

interface Telink3228CProfileSnapshotPayload {
  schema: typeof PROFILE_SNAPSHOT_SCHEMA
  configName: string
  firmwareVersion: string
  sourceProfileIndex: number
  functionArea: number[]
  keyArea: number[]
  macro: {
    macroList: Macro[]
  }
}

/** 名称区槽位未命名（0x00/0xFF 或非法 UTF-8）时的默认展示名；profile 0 固定为默认配置。 */
const DEFAULT_CONFIG_NAMES: readonly string[] = Array.from(
  { length: VISION_AP_MOUSE_FUNCTION_AREA.profileCount },
  (_, index) => (index === 0 ? '默认配置' : `配置 ${index + 1}`),
)

interface TelinkApMouseConfigDevice extends BaseDevice {
  restoreFactorySettings(): Promise<void>
  runFunctionAreaSession<T>(
    operation: (session: VisionApFunctionAreaSession) => Promise<T>,
    profileIndex?: number,
  ): Promise<T>
  runKeyAreaSession<T>(
    operation: (session: VisionApKeyAreaSession) => Promise<T>,
    profileIndex?: number,
  ): Promise<T>
  runProfileNameAreaSession<T>(
    operation: (session: VisionApProfileNameAreaSession) => Promise<T>,
  ): Promise<T>
}

/**
 * V0105 固件提供 8 个固定 profile，不支持创建与删除；
 * V0106 起名称持久化在 BB/BC 名称区（8 × 16 字节），重命名按整区读改写。
 */
export class Telink3228CMouseConfig extends ConfigFeature {
  private readonly apDevice: TelinkApMouseConfigDevice
  /** 最近一次从设备读回（或写后回读确认）的名称区；名称只会经 SDK 修改，会话内可复用。 */
  private profileNameArea?: Uint8Array
  private profileNameAreaInFlight?: Promise<Uint8Array>

  constructor(device: TelinkApMouseConfigDevice) {
    super(device)
    this.apDevice = device
  }

  public async getCurrentConfig(): Promise<Config> {
    const index = await this.getCurrentConfigIndex()
    await this.loadProfileNameArea()
    this.syncConfigs(index)
    return this.apDevice.currentConfig
  }

  public async getCurrentConfigIndex(): Promise<number> {
    return this.apDevice.runFunctionAreaSession(async (session) => {
      const area = await session.readFunctionAreaSnapshot()
      const index = area[VISION_AP_MOUSE_FUNCTION_AREA.currentProfileIndexOffset]
      assertVisionApProfileIndex(index)
      return index
    }, 0)
  }

  public async getCurrentConfigName(index?: number): Promise<string> {
    const targetIndex = index ?? (await this.getCurrentConfigIndex())
    assertVisionApProfileIndex(targetIndex)
    await this.loadProfileNameArea()
    return this.resolveConfigName(targetIndex)
  }

  public async getAllConfigs(): Promise<Config[]> {
    await this.loadProfileNameArea()
    this.apDevice.configList = this.buildConfigList()
    return this.apDevice.configList
  }

  public async setCurrentConfig(index: number): Promise<void> {
    assertVisionApProfileIndex(index)
    await this.apDevice.runFunctionAreaSession(async (session) => {
      const area = await session.readFunctionAreaSnapshot()
      area[VISION_AP_MOUSE_FUNCTION_AREA.currentProfileIndexOffset] = index
      await session.writeFunctionAreaSnapshot(area)
    }, 0)
    // 切换不额外读取名称区，沿用会话内已读回的名称；Web 随后的 getCurrentConfig 会补齐。
    this.syncConfigs(index)
  }

  /**
   * 重命名 profile：V0106 §2.2.17 要求即使只改一个名称也要整区写满 128 字节，
   * 因此先 BB 读回整区、只替换目标 16 字节槽位，再 BC 回写并以 BB 回读确认。
   */
  public async renameConfig(config: Config): Promise<void> {
    assertVisionApProfileIndex(config.index)
    const slot = encodeVisionApProfileName(config.name)
    const offset = getVisionApProfileNameSlotOffset(config.index)
    const area = await this.loadProfileNameArea()
    // 名称只会经 SDK 修改，基于会话内快照改写目标槽位即可，其余 7 槽原样保留。
    const next = area.slice()
    next.set(slot, offset)

    try {
      this.profileNameArea = await this.apDevice.runProfileNameAreaSession(async (session) => {
        await session.writeProfileNameArea(next)
        const readBack = await session.readProfileNameArea()
        this.assertProfileNameAreaStored(readBack, next)
        return readBack
      })
    } catch (error) {
      // 写入或回读失败后设备上的名称区状态未知，丢弃快照，下次读取时重新回读。
      this.profileNameArea = undefined
      throw error
    }
    this.syncConfigs(this.apDevice.currentConfig.index)
  }

  public async initAllConfigs(): Promise<void> {
    await this.apDevice.restoreFactorySettings()
    // 恢复出厂后名称区是否被固件重置未在协议中说明，丢弃会话缓存并在下次读取时重新回读。
    this.profileNameArea = undefined
    this.syncConfigs(0)
  }

  /**
   * 上传当前 profile 的完整协议快照。
   *
   * 功能区与按键区均保留完整 128 字节，避免丢失暂未建模的协议字段；宏区按
   * 现有 mouseKeys contract 导出完整宏详情，与 54 系列云配置行为保持一致。
   */
  public async getCurrentConfigSnapshot(): Promise<DeviceDataSnapshot> {
    const config = await this.getCurrentConfig()
    const firmware = this.features.firmware
    const mouseKeys = this.features.mouseKeys
    if (!firmware || !mouseKeys) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用 SDK 功能不完整')
    }

    const updateProgress = (progress: number) => {
      this.apDevice.emit('config:Upload_current_config', progress)
    }
    updateProgress(0)

    const firmwareVersion = formatFirmwareVersions(await firmware.getFirmwareVersion())
    updateProgress(10)

    const functionArea = await this.apDevice.runFunctionAreaSession(
      (session) => session.readFunctionAreaSnapshot(),
      config.index,
    )
    updateProgress(40)

    const keyArea = await this.apDevice.runKeyAreaSession(
      (session) => this.readKeyAreaSnapshot(session),
      config.index,
    )
    updateProgress(70)

    const macroList: Macro[] = []
    for await (const macroChunk of mouseKeys.getMacroListStream()) {
      macroList.push(...macroChunk)
    }
    const macroDetails: Macro[] = []
    for (let index = 0; index < macroList.length; index += 1) {
      const macroIndex = macroList[index]?.index
      if (macroIndex === undefined) continue
      macroDetails.push(await mouseKeys.getMacroByIndex(macroIndex))
      updateProgress(70 + Math.round((30 * (index + 1)) / Math.max(macroList.length, 1)))
    }
    updateProgress(100)

    const payload: Telink3228CProfileSnapshotPayload = {
      schema: PROFILE_SNAPSHOT_SCHEMA,
      configName: config.name,
      firmwareVersion,
      sourceProfileIndex: config.index,
      functionArea: Array.from(functionArea),
      keyArea: Array.from(keyArea),
      macro: { macroList: macroDetails },
    }
    return {
      metadata: {
        deviceType: this.apDevice.manifest.type,
        deviceModel: this.apDevice.manifest.model,
        timestamp: Date.now(),
        sdkVersion: this.apDevice.sdkVersion,
      },
      payload,
    }
  }

  /** 将云端快照完整写回当前选中的 profile。 */
  public async setCurrentConfigSnapshot(snapshot: DeviceDataSnapshot): Promise<void> {
    const { metadata, payload } = snapshot as DeviceDataSnapshot<Telink3228CProfileSnapshotPayload>
    this.assertSnapshotMetadata(metadata)
    if (!payload || payload.schema !== PROFILE_SNAPSHOT_SCHEMA) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '配置快照格式不匹配')
    }

    const mouseKeys = this.features.mouseKeys
    if (!mouseKeys) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用 SDK 功能不完整')
    }
    const functionArea = this.toProtocolArea(
      payload.functionArea,
      VISION_AP_MOUSE_FUNCTION_AREA.byteLength,
      '功能区',
    )
    const keyArea = this.toProtocolArea(
      payload.keyArea,
      VISION_AP_MOUSE_FUNCTION_AREA.byteLength,
      '按键区',
    )
    if (!payload.macro || !Array.isArray(payload.macro.macroList)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '配置快照缺少宏列表')
    }

    const target = await this.getCurrentConfig()
    // profile 0 的 byte 0 是全局当前 profile 选择器，下载 profile 0 时不能被快照旧值覆盖。
    if (target.index === 0) {
      functionArea[VISION_AP_MOUSE_FUNCTION_AREA.currentProfileIndexOffset] = target.index
    }

    const updateProgress = (progress: number) => {
      this.apDevice.emit('config:Download_current_config', Math.round(progress))
    }
    updateProgress(0)

    await this.apDevice.runFunctionAreaSession(
      (session) => session.writeFunctionAreaSnapshot(functionArea),
      target.index,
    )
    updateProgress(40)

    await this.apDevice.runKeyAreaSession(
      (session) => this.writeKeyAreaSnapshot(session, keyArea),
      target.index,
    )
    updateProgress(70)

    for (let index = 0; index < payload.macro.macroList.length; index += 1) {
      const macro = payload.macro.macroList[index]
      if (!macro || !Number.isInteger(macro.index) || macro.index! < 0) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '配置快照包含无效宏索引')
      }
      await mouseKeys.setMacroByIndex(macro, macro.index!)
      updateProgress(
        70 + Math.round((30 * (index + 1)) / Math.max(payload.macro.macroList.length, 1)),
      )
    }
    updateProgress(100)
  }

  private async readKeyAreaSnapshot(session: VisionApKeyAreaSession): Promise<Uint8Array> {
    const area = new Uint8Array(VISION_AP_MOUSE_FUNCTION_AREA.byteLength)
    let offset = 0
    for (const length of PROFILE_AREA_CHUNK_LENGTHS) {
      area.set(await session.readKeyArea(offset, length), offset)
      offset += length
    }
    return area
  }

  private async writeKeyAreaSnapshot(
    session: VisionApKeyAreaSession,
    area: Uint8Array,
  ): Promise<void> {
    let offset = 0
    for (const length of PROFILE_AREA_CHUNK_LENGTHS) {
      await session.writeKeyArea(offset, area.slice(offset, offset + length))
      offset += length
    }
  }

  private toProtocolArea(value: unknown, length: number, field: string): Uint8Array {
    if (
      !Array.isArray(value) ||
      value.length !== length ||
      value.some((byte) => !Number.isInteger(byte) || byte < 0 || byte > 0xff)
    ) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `${field}快照必须包含 ${length} 个有效字节`,
      )
    }
    return Uint8Array.from(value)
  }

  private assertSnapshotMetadata(metadata: DeviceDataSnapshot['metadata'] | undefined): void {
    if (!metadata || !metadata.sdkVersion) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用 SDK 版本不匹配')
    }
    if (
      metadata.deviceModel !== this.apDevice.manifest.model ||
      metadata.deviceType !== this.apDevice.manifest.type
    ) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '配置快照与当前设备不匹配')
    }
  }

  /**
   * 读取名称区并缓存在会话内；同时到达的读取合并为一次 BB 传输。
   */
  private async loadProfileNameArea(): Promise<Uint8Array> {
    if (this.profileNameArea) return this.profileNameArea
    if (!this.profileNameAreaInFlight) {
      this.profileNameAreaInFlight = this.apDevice
        .runProfileNameAreaSession((session) => session.readProfileNameArea())
        .then((area) => {
          this.profileNameArea = area
          return area
        })
        .finally(() => {
          this.profileNameAreaInFlight = undefined
        })
    }
    return this.profileNameAreaInFlight
  }

  private resolveConfigName(index: number): string {
    const area = this.profileNameArea
    if (area) {
      const offset = getVisionApProfileNameSlotOffset(index)
      const name = decodeVisionApProfileName(
        area.subarray(offset, offset + VISION_AP_MOUSE_PROFILE_NAME_AREA.slotByteLength),
      )
      if (name !== null) return name
    }
    return DEFAULT_CONFIG_NAMES[index]!
  }

  private buildConfigList(): Config[] {
    return DEFAULT_CONFIG_NAMES.map((_, index) => ({ index, name: this.resolveConfigName(index) }))
  }

  private assertProfileNameAreaStored(actual: Uint8Array, expected: Uint8Array): void {
    for (let offset = 0; offset < expected.byteLength; offset += 1) {
      if (actual[offset] !== expected[offset]) {
        throw new DeviceError(
          DeviceErrorCode.WRITE_FAILED,
          `profile 名称区写入后回读不一致（offset=${offset}）`,
        )
      }
    }
  }

  private syncConfigs(currentIndex: number): void {
    this.apDevice.configList = this.buildConfigList()
    this.apDevice.currentConfig = {
      index: currentIndex,
      name: this.resolveConfigName(currentIndex),
    }
  }
}
